import { z } from "zod";

export const companySchema = z.object({
  portal_uid: z.string(),
  chain_code: z.number(),
  company_name: z.string().nullable(),
  // Null when the user has no PORTALUSER yet — the company is offered via
  // app-driven discovery and will be JIT-provisioned on selection (ZC-5579).
  portal_user_public_id: z.string().nullable(),
  // When true, selecting this company provisions a user (if enabled) or is
  // denied (if not). Optional for backwards compatibility with older responses.
  requires_provisioning: z.boolean().optional().default(false),
  auto_provisioning_enabled: z.boolean().optional().default(false),
  // Authoritative support-flow signal (ZC-5658): true only for the Zen Support
  // IdP's synthesized chains. The SPA routes the support chain-switcher on this
  // flag rather than inferring it from chain_code === -1 (which master-chain
  // admins legitimately carry in the password flow). Optional for backwards
  // compatibility with older responses.
  is_support: z.boolean().optional().default(false),
});

export const authMethodSchema = z.object({
  type: z.enum(["password", "sso"]),
  idp_name: z.string().optional(),
  redirect_url: z.string().optional(),
  internal: z.boolean().optional(),
  // e.g. "linked" | "domain_match" | "support" (Zen Support IdP, ZC-5604)
  source: z.string().optional(),
});

export const lookupResponseSchema = z.object({
  found: z.boolean(),
  verified: z.boolean().nullable(),
  identity_id: z.string().nullable(),
  auth_methods: z.array(authMethodSchema),
});

export const verifyResponseSchema = z.object({
  verified: z.boolean(),
});

export const authenticateResponseSchema = z.object({
  authenticated: z.boolean(),
  companies: z.array(companySchema).nullable(),
  redirect_uri: z.string().nullable(),
});

export const selectCompanyResponseSchema = z.object({
  redirect_uri: z.string().nullable(),
});

export type Company = z.infer<typeof companySchema>;
export type AuthMethod = z.infer<typeof authMethodSchema>;
export type LookupResponse = z.infer<typeof lookupResponseSchema>;
export type VerifyResponse = z.infer<typeof verifyResponseSchema>;
export type AuthenticateResponse = z.infer<typeof authenticateResponseSchema>;
export type SelectCompanyResponse = z.infer<typeof selectCompanyResponseSchema>;

export const validateInviteValidResponseSchema = z.object({
  valid: z.literal(true),
  email: z.string(),
  company_name: z.string().nullable(),
  type: z.enum(["invite", "password_reset"]),
});

export const validateInviteInvalidResponseSchema = z.object({
  valid: z.literal(false),
  expired: z.boolean(),
  email: z.string().optional(),
});

export const validateInviteResponseSchema = z.discriminatedUnion("valid", [
  validateInviteValidResponseSchema,
  validateInviteInvalidResponseSchema,
]);

export const acceptInviteResponseSchema = z.object({
  redirect_uri: z.string().nullable(),
});

export const deviceApprovalPayloadSchema = z.object({
  user_code: z.string(),
  identity_id: z.string(),
  portal_uid: z.string(),
  chain_code: z.number(),
  approved: z.boolean(),
});

export const completeDeviceApprovalResponseSchema = z.object({
  status: z.string(),
});

export const lookupDeviceResponseSchema = z.object({
  client_name: z.string(),
  scopes: z.array(z.string()),
  status: z.string(),
});

export type ValidateInviteResponse = z.infer<
  typeof validateInviteResponseSchema
>;
export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>;
export type DeviceApprovalPayload = z.infer<typeof deviceApprovalPayloadSchema>;
export type CompleteDeviceApprovalResponse = z.infer<
  typeof completeDeviceApprovalResponseSchema
>;
export type LookupDeviceResponse = z.infer<typeof lookupDeviceResponseSchema>;
